// src/components/Header/index.js
import React, { useState, useEffect, useMemo } from 'react';
import './Header.css';

const Header = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPrivacyPolicy, setIsPrivacyPolicy] = useState(false);
  
  // Navigation items wrapped in useMemo to prevent recreating on every render
  const allNavItems = useMemo(() => [
    { id: 'frame-95', text: '生活保護とは', sectionId: 'infoSection' },
    { id: 'frame-96', text: '選ばれる理由', sectionId: 'reasonsSection' },
    // { id: 'frame-100', text: '物件情報', sectionId: 'propertyList' }, 
    { id: 'frame-99x', text: 'サポートの流れ', sectionId: 'supportFlow' },
    { id: 'frame-101', text: 'よくある質問', sectionId: 'faqSection' },
    { id: 'frame-102', text: 'ブログ', sectionId: 'blogSection' }
  ], []); // Empty dependency array means this will only be created once

  // Check if we're on the privacy policy page
  useEffect(() => {
    const checkPrivacyPage = () => {
      const url = new URL(window.location.href);
      setIsPrivacyPolicy(url.searchParams.get('page') === 'privacy');
    };
    
    // Check on initial load
    checkPrivacyPage();
    
    // Add event listener for URL changes
    window.addEventListener('popstate', checkPrivacyPage);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('popstate', checkPrivacyPage);
    };
  }, []);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false); // Close mobile menu when resizing to desktop
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Prevent scrolling when menu is open
    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  // Handle smooth scroll to section
  const scrollToSection = (sectionId) => {
    // First check if we're on the privacy policy page
    if (isPrivacyPolicy) {
      // First go back to the home page
      window.history.pushState({}, '', '/');
      // Trigger a popstate event to update App.js state
      window.dispatchEvent(new Event('popstate'));
      
      // Need a short delay to ensure the main page content is rendered
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          // Calculate header height for offset
          const headerHeight = document.querySelector('.header-nav-container')?.offsetHeight || 0;
          
          // Scroll to section with offset for header
          window.scrollTo({
            top: section.offsetTop - headerHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // We're already on the home page, just scroll to the section
      const section = document.getElementById(sectionId);
      if (section) {
        // Close mobile menu if it's open
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
          document.body.style.overflow = 'auto';
        }
        
        // Calculate header height for offset
        const headerHeight = document.querySelector('.header-nav-container')?.offsetHeight || 0;
        
        // Scroll to section with offset for header
        window.scrollTo({
          top: section.offsetTop - headerHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  // Scroll to top of page (for logo click)
  const scrollToTop = () => {
    // If we're on the privacy policy page, navigate back to home
    if (isPrivacyPolicy) {
      // Change URL to home page without the privacy parameter
      window.history.pushState({}, '', '/');
      // Trigger a popstate event to update App.js state
      window.dispatchEvent(new Event('popstate'));
    }
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Close mobile menu if it's open
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      document.body.style.overflow = 'auto';
    }
  };

  // Determine if mobile view should be shown
  const isMobileView = windowWidth <= 768;

  // Desktop navigation logic
  let visibleItems = [];
  let dropdownItems = [];

  if (!isMobileView) {
    if (windowWidth > 1400) {
      // Full view - all items visible
      visibleItems = [...allNavItems];
      dropdownItems = [];
    } else if (windowWidth > 1200) {
      // Hide last two items
      visibleItems = allNavItems.slice(0, 4);
      dropdownItems = allNavItems.slice(4);
    } else if (windowWidth > 1000) {
      // Hide last three items
      visibleItems = allNavItems.slice(0, 3);
      dropdownItems = allNavItems.slice(3);
    } else {
      // Hide last four items
      visibleItems = allNavItems.slice(0, 2);
      dropdownItems = allNavItems.slice(2);
    }
  }

  // Active section detection
  useEffect(() => {
    // Function to highlight active sections based on scroll position
    const highlightActiveSection = () => {
      // Don't highlight sections on privacy policy page
      if (isPrivacyPolicy) return;
      
      // Get all sections we want to track
      const sections = allNavItems.map(item => document.getElementById(item.sectionId)).filter(Boolean);
      
      // Get current scroll position with buffer for header
      const headerHeight = document.querySelector('.header-nav-container')?.offsetHeight || 0;
      const scrollPosition = window.scrollY + headerHeight + 50; // Add some buffer
      
      // Find the current section
      let currentSection = null;
      for (const section of sections) {
        if (section && section.offsetTop <= scrollPosition && 
            (section.offsetTop + section.offsetHeight) > scrollPosition) {
          currentSection = section.id;
          break;
        }
      }
      
      // Remove active class from all nav items
      document.querySelectorAll('.header-nav-item-container, .header-dropdown-item')
        .forEach(item => item.classList.remove('active'));
      
      // Add active class to current section's nav item
      if (currentSection) {
        const navItem = allNavItems.find(item => item.sectionId === currentSection);
        if (navItem) {
          document.getElementById(navItem.id)?.classList.add('active');
        }
      }
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', highlightActiveSection);
    
    // Call once to set initial state
    highlightActiveSection();
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', highlightActiveSection);
    };
  }, [allNavItems, isPrivacyPolicy]); // Also depend on isPrivacyPolicy

  return (
    <>
      <header className="header-nav-container">
        <div className="header-frame header-frame-100">
          {/* Logo container with home link */}
          <div className="header-logo-container">
            <a href="/" onClick={(e) => {
              e.preventDefault();
              scrollToTop();
            }} aria-label="ホームに戻る">
              <img src={process.env.PUBLIC_URL + '/images/logo.png'} alt="ほごらんど Logo" className="header-logo" />
            </a>
          </div>
          
          {/* Rest of the Header component remains the same */}
          {/* Desktop View */}
          {!isMobileView && (
            <div className="header-frame header-frame-115">
              {/* Navigation Menu */}
              <div className="header-frame header-frame-99">
                {/* Visible Navigation Items */}
                {visibleItems.map(item => (
                  <div 
                    key={item.id} 
                    className="header-nav-item-container" 
                    id={item.id}
                    onClick={() => scrollToSection(item.sectionId)}
                  >
                    <span className="header-nav-text">{item.text}</span>
                    <div className="header-arrow-indicator">
                      <svg width="20" height="8" viewBox="0 0 20 8" className="header-arrow-svg">
                        <path 
                          d="M1.58105 1.51932L10.2635 6.48071" 
                          stroke="#949494" 
                          strokeWidth="2" 
                          strokeLinecap="round"
                        />
                        <path 
                          d="M18.4458 1.51932L9.76337 6.48071" 
                          stroke="#949494" 
                          strokeWidth="2" 
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                ))}
                
                {/* Dropdown Menu (if there are items to hide) */}
                {dropdownItems.length > 0 && (
                  <div className="header-dropdown-container">
                    <button 
                      className="header-dropdown-toggle" 
                      onClick={toggleMobileMenu}
                      aria-expanded={isMobileMenuOpen}
                      aria-label="Menu"
                    >
                      {/* Burger Icon */}
                      <div className={`header-burger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
                        <span className="header-burger-line"></span>
                        <span className="header-burger-line"></span>
                        <span className="header-burger-line"></span>
                      </div>
                    </button>
                    
                    {isMobileMenuOpen && (
                      <div className="header-dropdown-menu">
                        {dropdownItems.map(item => (
                          <div 
                            key={item.id} 
                            className="header-dropdown-item" 
                            id={item.id}
                            onClick={() => scrollToSection(item.sectionId)}
                          >
                            <span className="header-nav-text">{item.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* CTA Button */}
              <a href="https://hogoland.net/simulator/" target="_blank" rel="noopener noreferrer" className="header-frame header-frame-106" style={{ textDecoration: 'none' }}>
                <div className="header-frame header-frame-105">
                  {/* Task List Icon */}
                  <div className="header-icon header-task-list-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M15.067 16.0714C15.067 16.4124 14.9315 16.7395 14.6904 16.9806C14.4492 17.2216 14.1223 17.3571 13.7813 17.3571H2.20979C1.86879 17.3571 1.54177 17.2216 1.30065 16.9806C1.05953 16.7395 0.924072 16.4124 0.924072 16.0714V1.92857C0.924072 1.58757 1.05953 1.26055 1.30065 1.01943C1.54177 0.778312 1.86879 0.642853 2.20979 0.642853H9.39152C9.7325 0.642853 10.0595 0.778312 10.3006 1.01943L14.6904 5.40914C14.9315 5.65025 15.067 5.97728 15.067 6.31827V16.0714Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8.71362 8.19641H11.9279" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8.71362 12.6562H11.9279" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3.7417 12.5834L4.81899 13.6607L6.61446 11.147" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3.7417 8.04323L4.81899 9.12052L6.61446 6.60684" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  
                  {/* Text and Subtract Icon */}
                  <div className="header-frame header-frame-135">
                    <div className="header-text-content">まずは無料診断</div>
                    <div className="header-icon header-subtract-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M9 18C13.9706 18 18 13.9706 18 9C18 4.02944 13.9706 0 9 0C4.02944 0 0 4.02944 0 9C0 13.9706 4.02944 18 9 18ZM11.2301 9.50935C11.7193 9.10925 11.7193 8.36134 11.2301 7.96124L9.04488 6.17393C8.39192 5.63986 7.41177 6.10443 7.41177 6.94798L7.41177 10.5226C7.41177 11.3662 8.39192 11.8307 9.04488 11.2967L11.2301 9.50935Z" fill="white"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          )}
          
          {/* Mobile View - Phone and Hamburger Icons */}
          {/* The rest of the component remains the same */}
          {isMobileView && (
            <div className="header-frame header-frame-173">
              {/* Phone Icon with Link */}
              <a href="tel:03-6737-7589" className="header-mobile-phone-container">
                <div className="header-mobile-phone-ellipse">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="14" fill="#5A4C49"/>
                  </svg>
                </div>
                <div className="header-mobile-phone-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M14.4803 11.189V13.1334C14.481 13.3139 14.444 13.4926 14.3717 13.658C14.2994 13.8234 14.1934 13.9719 14.0603 14.0939C13.9273 14.2159 13.7703 14.3088 13.5993 14.3667C13.4283 14.4245 13.2471 14.446 13.0673 14.4297C11.0729 14.213 9.15705 13.5315 7.47381 12.4399C5.90777 11.4448 4.58004 10.1171 3.58492 8.55102C2.48953 6.86014 1.80785 4.935 1.5951 2.93158C1.57891 2.75235 1.60021 2.5717 1.65765 2.40115C1.71509 2.2306 1.80742 2.07388 1.92875 1.94096C2.05008 1.80805 2.19776 1.70186 2.36238 1.62914C2.527 1.55643 2.70495 1.51879 2.88492 1.51862H4.82936C5.14391 1.51552 5.44886 1.62691 5.68736 1.83202C5.92586 2.03713 6.08164 2.32196 6.12566 2.63343C6.20773 3.2557 6.35993 3.86668 6.57936 4.45473C6.66657 4.68672 6.68544 4.93884 6.63375 5.18122C6.58205 5.42361 6.46196 5.6461 6.2877 5.82232L5.46455 6.64547C6.38722 8.26814 7.73077 9.61168 9.35344 10.5344L10.1766 9.71121C10.3528 9.53695 10.5753 9.41685 10.8177 9.36516C11.0601 9.31347 11.3122 9.33234 11.5442 9.41954C12.1322 9.63897 12.7432 9.79118 13.3655 9.87325C13.6803 9.91766 13.9679 10.0763 14.1734 10.3188C14.379 10.5614 14.4882 10.8711 14.4803 11.189Z" fill="white"/>
                  </svg>
                </div>
              </a>
              
              {/* Hamburger Button */}
              <button 
                className="header-mobile-hamburger-button"
                onClick={toggleMobileMenu}
                aria-expanded={isMobileMenuOpen}
                aria-label="Menu"
              >
                {!isMobileMenuOpen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="18" viewBox="0 0 26 18" fill="none">
                    <path d="M1 1H25" stroke="#5A4C49" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M1 9H25" stroke="#5A4C49" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M1 17H25" stroke="#5A4C49" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M17 1L1 17" stroke="#5A4C49" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M1 1L17 17" stroke="#5A4C49" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Full Screen Menu */}
      {isMobileView && isMobileMenuOpen && (
        <div className="header-mobile-menu-overlay">
          <div className="header-mobile-menu-container">
            {allNavItems.map(item => (
              <div 
                key={item.id} 
                className="header-mobile-menu-item"
                onClick={() => scrollToSection(item.sectionId)}
              >
                <div className="header-mobile-menu-item-content">
                  <span className="header-mobile-menu-text">{item.text}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="16" viewBox="0 0 7 16" fill="none" className="header-mobile-menu-arrow">
                    <path d="M1.69922 1.85449L5.99948 7.87486" stroke="#F4792B" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M1.69922 14.1455L5.99948 8.12514" stroke="#F4792B" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
          
          <div className="header-mobile-phone-info">
            <div className="header-mobile-contact-container">
              <div className="header-mobile-dial-container">
                <div className="header-mobile-dial-info">
                  <div className="header-mobile-dial-title-container">
                    <div className="header-mobile-phone-badge">
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <g clipPath="url(#clip0_43_1857)">
                          <path d="M9.16562 7.0502V8.3002C9.16609 8.41625 9.14232 8.53111 9.09583 8.63743C9.04935 8.74376 8.98116 8.8392 8.89565 8.91765C8.81014 8.9961 8.70919 9.05582 8.59927 9.093C8.48934 9.13017 8.37286 9.14398 8.25729 9.13354C6.97514 8.99422 5.74354 8.5561 4.66145 7.85437C3.65472 7.21465 2.80118 6.36111 2.16145 5.35437C1.45728 4.26737 1.01906 3.02978 0.882288 1.74187C0.871876 1.62665 0.885569 1.51052 0.922497 1.40088C0.959424 1.29124 1.01878 1.19049 1.09677 1.10505C1.17477 1.0196 1.26971 0.951332 1.37553 0.904587C1.48136 0.857843 1.59576 0.833645 1.71145 0.833536H2.96145C3.16367 0.831546 3.3597 0.903153 3.51302 1.03501C3.66634 1.16686 3.76649 1.34997 3.79479 1.5502C3.84755 1.95023 3.94539 2.34301 4.08646 2.72104C4.14251 2.87017 4.15465 3.03225 4.12142 3.18807C4.08818 3.34389 4.01098 3.48692 3.89895 3.6002L3.36979 4.12937C3.96294 5.17251 4.82664 6.03622 5.86979 6.62937L6.39895 6.1002C6.51224 5.98818 6.65527 5.91097 6.81109 5.87774C6.96691 5.84451 7.12899 5.85664 7.27812 5.9127C7.65615 6.05377 8.04893 6.15161 8.44896 6.20437C8.65136 6.23292 8.83621 6.33487 8.96834 6.49083C9.10048 6.64678 9.17069 6.84586 9.16562 7.0502Z" fill="white"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_43_1857">
                            <rect width="10" height="10" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <div className="header-mobile-dial-title">ほごらんど専用ダイヤル</div>
                  </div>
                  <div className="header-mobile-dial-hours">(受付時間) 年中無休 10:00~19:00</div>
                  <div className="header-mobile-dial-number">
                    <a href="tel:03-6737-7589" className="header-phone-link">03-6737-7589</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;